import { LayoutGrid, List, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';

import { MAX_AUDIO_BYTES } from '../shared/contracts';
import { type ApiClient, browserApi, type SessionUserDto, type TrackDto } from './api';
import {
  clampSpectrumParams,
  DEFAULT_SPECTRUM_PARAMS,
  type SpectrumParams,
} from './audio/spectrum';
import {
  clampParams,
  DEFAULT_WAVEFORM_PARAMS,
  type WaveformParams,
} from './audio/waveform-render';
import { AdminPanel } from './components/AdminPanel';
import { errorMessage, formatBytes } from './components/format';
import { LibraryGrid, type LibraryLayout } from './components/LibraryGrid';
import { PlayerDock } from './components/player/PlayerDock';
import { SignInDialog } from './components/SignInDialog';
import { TopBar } from './components/TopBar';
import { TrackEditor } from './components/TrackEditor';
import { TrackView } from './components/TrackView';
import { UploadTray, type UploadView } from './components/UploadTray';
import { usePlayer } from './hooks/usePlayer';
import { trackPath, useRoute } from './hooks/useRoute';
import { useStoredState } from './hooks/useStoredState';
import { useWaveform } from './hooks/useWaveform';
import {
  IndexedDbUploadPersistence,
  uploadMp3,
  type UploadApi,
  type UploadPersistence,
} from './uploader';

/** Loading is distinct from "signed out" so the shell does not flash a Sign in button. */
type SessionState =
  | { status: 'loading' }
  | { status: 'ready'; user: SessionUserDto | null; pendingUserCount?: number };

type AppProps = {
  api?: ApiClient;
  persistence?: UploadPersistence;
};

export function App({ api = browserApi, persistence }: AppProps) {
  const [session, setSession] = useState<SessionState>({ status: 'loading' });
  const [tracks, setTracks] = useState<TrackDto[]>([]);
  const [storage, setStorage] = useState<{
    objectCount: number;
    totalBytes: number;
    quotaBytes?: number;
  } | null>(null);
  const [view, setView] = useState<'library' | 'trash'>('library');
  const [sort, setSort] = useState<'newest' | 'title' | 'year' | 'duration'>('newest');
  const [query, setQuery] = useState('');
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadView[]>([]);
  const [editing, setEditing] = useState<TrackDto | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const persistenceRef = useRef<UploadPersistence | null>(persistence ?? null);

  const { route, navigate } = useRoute();
  const player = usePlayer(api);

  const [waveformParams, setWaveformParams] = useStoredState<WaveformParams>(
    'psc.waveformParams',
    DEFAULT_WAVEFORM_PARAMS,
    (value) => clampParams({ ...DEFAULT_WAVEFORM_PARAMS, ...(value as Partial<WaveformParams>) }),
  );
  const [spectrumParams, setSpectrumParams] = useStoredState<SpectrumParams>(
    'psc.spectrumParams',
    DEFAULT_SPECTRUM_PARAMS,
    (value) =>
      clampSpectrumParams({ ...DEFAULT_SPECTRUM_PARAMS, ...(value as Partial<SpectrumParams>) }),
  );
  // Rows by default: a long set's name and duration say more at a glance than its
  // artwork does, and the grid spends most of its height on artwork.
  const [layout, setLayout] = useStoredState<LibraryLayout>(
    'psc.libraryLayout',
    'list',
    (value) => (value === 'grid' ? 'grid' : 'list'),
  );
  const [analyserOpen, setAnalyserOpen] = useStoredState<boolean>(
    'psc.analyserOpen',
    true,
    (value) => value !== false,
  );

  const user = session.status === 'ready' ? session.user : null;
  const isOwner = user?.role === 'owner';
  const pendingUserCount = session.status === 'ready' ? (session.pendingUserCount ?? 0) : 0;

  const refreshSession = useCallback(async () => {
    try {
      const result = await api.session();
      setSession({ status: 'ready', ...result });
    } catch {
      setSession({ status: 'ready', user: null });
    }
  }, [api]);

  useEffect(() => {
    let active = true;
    void api.session().then(
      (result) => active && setSession({ status: 'ready', ...result }),
      () => active && setSession({ status: 'ready', user: null }),
    );
    return () => {
      active = false;
    };
  }, [api]);

  const loadTracks = useCallback(async () => {
    if (session.status === 'loading') return;
    setLoadingTracks(true);
    try {
      const searchQuery = query.trim();
      const result = await api.listTracks({
        view,
        sort,
        ...(searchQuery ? { query: searchQuery } : {}),
      });
      setTracks(result.items);
      setStorage(result.storage ?? null);
      setNotice(null);
    } catch (error) {
      setNotice(errorMessage(error));
    } finally {
      setLoadingTracks(false);
    }
  }, [api, query, session.status, sort, view]);

  useEffect(() => {
    const timer = setTimeout(() => void loadTracks(), 150);
    return () => clearTimeout(timer);
  }, [loadTracks]);

  // Poll while anything is still being processed or analysed, so cards and waveforms
  // fill themselves in without a manual refresh.
  useEffect(() => {
    const settling = tracks.some(
      (track) =>
        track.status === 'processing' || track.status === 'uploading' || !track.hasWaveform,
    );
    if (!settling) return;
    const timer = setInterval(() => void loadTracks(), 8_000);
    return () => clearInterval(timer);
  }, [loadTracks, tracks]);

  const openTrack = route.name === 'track' ? tracks.find((track) => track.id === route.id) : undefined;
  const [fetchedTrack, setFetchedTrack] = useState<TrackDto | null>(null);

  // A shared link lands directly on /t/:id, which may not be in the current listing
  // (different sort, a search filter, or a cold load) — fetch it on its own.
  useEffect(() => {
    if (route.name !== 'track' || openTrack) {
      setFetchedTrack(null);
      return;
    }
    let active = true;
    void fetch(`/api/tracks/${encodeURIComponent(route.id)}`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (active) setFetchedTrack((payload?.data as TrackDto | undefined) ?? null);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [openTrack, route]);

  const activeTrack = openTrack ?? fetchedTrack;

  // Only the track view draws a waveform; the dock shows the live analyser.
  const viewWaveform = useWaveform(
    route.name === 'track' ? (activeTrack?.id ?? null) : null,
    activeTrack?.hasWaveform ?? false,
  );

  async function handleSignIn(username: string, password: string) {
    await api.signIn(username, password);
    // Re-read rather than assume: the server decides the role and the pending count.
    await refreshSession();
    setSigningIn(false);
    await loadTracks();
  }

  async function handleSignOut() {
    await api.signOut();
    setSession({ status: 'ready', user: null });
    setView('library');
    await loadTracks();
  }

  function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.mp3') || file.type !== 'audio/mpeg') {
        setNotice(`${file.name} is not an MP3 file.`);
        continue;
      }
      if (file.size > MAX_AUDIO_BYTES) {
        setNotice(`${file.name} is larger than 1 GiB.`);
        continue;
      }
      void startUpload(file);
    }
  }

  async function startUpload(file: File) {
    if (!persistenceRef.current) persistenceRef.current = new IndexedDbUploadPersistence();
    const key = `${file.name}:${file.size}:${file.lastModified}`;
    const controller = new AbortController();
    setUploads((current) => [
      ...current.filter((upload) => upload.key !== key),
      { key, filename: file.name, percent: 0, state: 'uploading', controller },
    ]);
    const uploadApi: UploadApi = {
      create: (input) => api.createUpload(input),
      status: (id) => api.uploadStatus(id),
      signParts: (id, parts) => api.signUploadParts(id, parts),
      complete: (id, parts) => api.completeUpload(id, parts),
      cancel: (id) => api.cancelUpload(id),
    };
    try {
      await uploadMp3(file, {
        api: uploadApi,
        persistence: persistenceRef.current,
        signal: controller.signal,
        onProgress: ({ percent }) =>
          setUploads((current) =>
            current.map((upload) => (upload.key === key ? { ...upload, percent } : upload)),
          ),
      });
      setUploads((current) =>
        current.map((upload) =>
          upload.key === key ? { ...upload, percent: 100, state: 'processing' } : upload,
        ),
      );
      await loadTracks();
      setTimeout(
        () => setUploads((current) => current.filter((upload) => upload.key !== key)),
        5_000,
      );
    } catch (error) {
      setUploads((current) =>
        current.map((upload) =>
          upload.key === key
            ? {
                ...upload,
                state: 'error',
                error: controller.signal.aborted ? 'Upload cancelled' : errorMessage(error),
              }
            : upload,
        ),
      );
    }
  }

  // Transport shortcuts. Skipped while typing so a search box never eats a spacebar.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (!player.track) return;

      if (event.code === 'Space') {
        event.preventDefault();
        player.toggle();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        player.skip(-player.skipSeconds);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        player.skip(player.skipSeconds);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        player.setVolume(player.volume + 0.05);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        player.setVolume(player.volume - 0.05);
      } else if (event.key === 'm' || event.key === 'M') {
        player.toggleMute();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [player]);

  useEffect(() => {
    if (player.error) setNotice(player.error);
  }, [player.error]);

  if (session.status === 'loading') {
    return (
      <main className="center-stage">
        <div className="loader" aria-label="Loading" />
      </main>
    );
  }

  return (
    <div className="app">
      <TopBar
        query={query}
        onQueryChange={setQuery}
        onHome={() => {
          setView('library');
          navigate('/');
        }}
        isOwner={isOwner}
        username={user?.displayUsername ?? user?.username ?? null}
        pendingUserCount={pendingUserCount}
        onOpenAdmin={() => navigate('/admin')}
        onSignIn={() => setSigningIn(true)}
        onSignOut={() => void handleSignOut()}
        onUpload={handleFiles}
        onOpenTrash={() => {
          setView('trash');
          navigate('/');
        }}
        onBackup={() =>
          void api
            .createBackup()
            .then(() => setNotice('Catalog backup created.'))
            .catch((error: unknown) => setNotice(errorMessage(error)))
        }
      />

      {route.name === 'admin' && isOwner ? (
        <AdminPanel api={api} onChanged={() => void refreshSession()} />
      ) : route.name === 'track' && activeTrack ? (
        <TrackView
          track={activeTrack}
          player={player}
          waveform={viewWaveform.data}
          waveformPending={viewWaveform.pending}
          waveformParams={waveformParams}
          spectrumParams={spectrumParams}
          onWaveformParams={setWaveformParams}
          onSpectrumParams={setSpectrumParams}
          onBack={() => navigate('/')}
        />
      ) : (
        <main className="library">
          <div className="library-head">
            <div>
              <h1>{view === 'library' ? 'Sets' : 'Trash'}</h1>
              <p className="numeric">
                {view === 'trash' && 'Recoverable for seven days · '}
                {storage
                  ? `${formatBytes(storage.totalBytes)} stored · ${storage.objectCount} ${
                      storage.objectCount === 1 ? 'set' : 'sets'
                    }`
                  : `${tracks.length} ${tracks.length === 1 ? 'set' : 'sets'}`}
              </p>
            </div>
            <div className="library-controls">
              {user && (
                <div className="segmented" role="group" aria-label="Library view">
                  <button
                    type="button"
                    className={view === 'library' ? 'is-active' : ''}
                    onClick={() => setView('library')}
                  >
                    Library
                  </button>
                  <button
                    type="button"
                    className={view === 'trash' ? 'is-active' : ''}
                    onClick={() => setView('trash')}
                  >
                    Trash
                  </button>
                </div>
              )}
              <div className="segmented layout-toggle" role="group" aria-label="Library layout">
                <button
                  type="button"
                  className={layout === 'list' ? 'is-active' : ''}
                  onClick={() => setLayout('list')}
                  aria-pressed={layout === 'list'}
                  aria-label="List layout"
                  title="List"
                >
                  <List size={15} />
                </button>
                <button
                  type="button"
                  className={layout === 'grid' ? 'is-active' : ''}
                  onClick={() => setLayout('grid')}
                  aria-pressed={layout === 'grid'}
                  aria-label="Grid layout"
                  title="Grid"
                >
                  <LayoutGrid size={15} />
                </button>
              </div>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as typeof sort)}
                aria-label="Sort sets"
              >
                <option value="newest">Newest</option>
                <option value="title">Title</option>
                <option value="year">Year</option>
                <option value="duration">Duration</option>
              </select>
            </div>
          </div>

          {notice && (
            <div className="notice" role="status">
              <span>{notice}</span>
              <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss message">
                <X size={14} />
              </button>
            </div>
          )}

          <UploadTray uploads={uploads} />

          <LibraryGrid
            tracks={tracks}
            view={view}
            layout={layout}
            signedIn={Boolean(user)}
            loading={loadingTracks}
            playingId={player.track?.id ?? null}
            isPlaying={player.playing}
            onPlay={(track) => void player.play(track)}
            onOpen={navigate}
            onEdit={setEditing}
            onTrash={(track) =>
              void api
                .trashTrack(track.id)
                .then(loadTracks)
                .catch((error: unknown) => setNotice(errorMessage(error)))
            }
            onRestore={(track) =>
              void api
                .restoreTrack(track.id)
                .then(loadTracks)
                .catch((error: unknown) => setNotice(errorMessage(error)))
            }
          />
        </main>
      )}

      {editing && (
        <TrackEditor
          track={editing}
          api={api}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void loadTracks();
          }}
        />
      )}

      {signingIn && (
        <SignInDialog
          onSignIn={handleSignIn}
          onSignUp={(input) => api.signUp(input)}
          onClose={() => setSigningIn(false)}
        />
      )}

      <PlayerDock
        player={player}
        spectrumParams={spectrumParams}
        analyserOpen={analyserOpen}
        onAnalyserOpen={setAnalyserOpen}
        onNavigate={navigate}
      />
    </div>
  );
}

export { trackPath };
